import {WsEvent} from "../support/CaseHook";
import React, {useTransition} from "react";
import {
    CaseRenamingEndEvent,
    ParallelSearchEndEvent,
    ParallelSearchProgressEvent,
    ParallelSearchStartEvent,
    RerankingEndEvent, RouteSelectionEndEvent,
    SearchEndEvent,
    SearchInQueueEvent,
    VersionInfoEndEvent
} from "./ProgressEvents";
import {useTranslation} from "react-i18next";

export function SearchWaitingProgress({events}: { events: WsEvent[] }) {
    const searchEndEvent = getEnd(events) as SearchEndEvent
    const searchInQueueEvents = events.filter(event => event.position === "progress") as SearchInQueueEvent[];

    const hasEnd = searchEndEvent !== undefined;
    if (hasEnd || searchInQueueEvents.length == 0) {
        return <div></div>
    }
    const minAdmissionCountDown = searchInQueueEvents.map(event => event.admission_count_down).reduce((a, b) => Math.min(a, b), Infinity);
    return (
        <div>
            <span>Similarity search capacity reached. Position in waiting queue: </span>
            <span className={"font-bold text-green-500"}>{minAdmissionCountDown}</span>
        </div>
    )
}

export function ParallelSearchProgress({events}: { events: WsEvent[] }) {
    const startEvent = getStart(events) as ParallelSearchStartEvent;
    const progressEvents = getAllProgress(events) as ParallelSearchProgressEvent[];
    const totalHits = progressEvents.reduce((acc, event) => acc + event.hit_count, 0)
    const endEvent = getEnd(events) as ParallelSearchEndEvent;
    const sourceCountPart = startEvent != null ? ` in ${startEvent.search_count} sources ` : " ";
    return (
        <div>
            <span className={"animate-fade"}>Searching{sourceCountPart}in parallel ...</span>
            {endEvent === undefined && progressEvents.length > 0 && (
                <span className={"animate-fade"}>
                        <span> Found </span>
                        <span className={"font-bold text-green-500"}>{totalHits}</span>
                        <span> hits so far ...</span>
                    </span>
            )}
            {endEvent !== undefined && (
                <span className={"animate-fade"}>
                    <span className={"font-bold text-green-500"}> finished</span>
                    <span>. Found {endEvent.hit_count} hits.</span>
                </span>
            )}
        </div>
    )
}

function getStart(events: WsEvent[]) {
    return events.find(event => event.position === "start");
}

function getEnd(events: WsEvent[]) {
    return events.find(event => event.position === "end");
}

function getAllProgress(events: WsEvent[]) {
    return events.filter(event => event.position === "progress");
}

export enum Activity {
    TECH_DOC_SEARCH = "tech_doc_search",
    COMM_CASE_SEARCH = "community_case_search",
    PARALLEL_SEARCH = "parallel_search",
    RESPONSE_GENERATION = "response_generator",
    VERSION_INFO = "version_info",
    RERANKING = "reranking",
    ROUTE_SELECTION = "route_selection",
    CASE_RENAMING = "renaming"

}

export function StartEndProgress({events, start, end}: {
    events: WsEvent[],
    start: React.ReactNode,
    end: React.ReactNode
}) {
    const startEvent = getStart(events);
    const endEvent = getEnd(events);
    if (endEvent !== undefined) {
        return end
    }
    return start
}

export function ResponseGenerationProgress({events}: { events: WsEvent[] }) {
    return <StartEndProgress
        events={events}
        start={<div><span className={"animate-fade"}> Generating response ...</span></div>}
        end={<div>
            <span className={"animate-fade"}> Generating response ...</span><span
            className={"font-bold text-green-500 animate-fade"}> finished</span>
        </div>}
    />
}

export function VersionInformationProgress({events}: { events: WsEvent[] }) {
    const endEvent = getEnd(events) as VersionInfoEndEvent;
    if (endEvent !== undefined) {
        const versions = Object.entries(endEvent.supplied_versions).map(([product, version]) => `${product}: ${version}`).join(", ")
        return (
            <div>
                <span className={"animate-fade"}> Analyzing product information ... </span>
                <span className={"animate-fade"}>
                    <span className={"font-bold text-green-500"}>finished</span>
                    <span>. Affected: {endEvent.affected_products.join(", ")}. Versions: {versions}</span>
                </span>
            </div>
        )
    }
    return <div><span className={"animate-fade"}> Analyzing product information ...</span></div>
}

export function RerankingProgress({events}: { events: WsEvent[] }) {
    const endEvent = getEnd(events) as RerankingEndEvent;
    const item_pairs = endEvent?.item_count_map != null ? Object.entries(endEvent.item_count_map).map(([key, count]) => `${key.toLowerCase().replace("_", " ")}: ${count}`).join(", ") : "";
    return (
        <div>
            <span className={"animate-fade"}>Analyzing search results ...</span>
            {endEvent !== undefined && (
                <span className={"animate-fade"}>
                    <span className={"font-bold text-green-500"}> finished</span>
                    <span>. Picked {item_pairs}.</span>
                </span>
            )
            }
        </div>
    )
}

export function RouteSelectionProgress({events}: { events: WsEvent[] }) {
    const {t} = useTranslation();
    const endEvent = getEnd(events) as RouteSelectionEndEvent;
    return (
        <div>
            <span className={"animate-fade"}>Identifying workflow ...</span>
            {endEvent !== undefined && (
                <span>
                    <span className={"font-bold text-green-500 animate-fade"}> finished</span>
                    <span>.</span>
                </span>
            )}


        </div>
    )
}

export function CaseRenamingProgress({events}: { events: WsEvent[] }) {
    const {t} = useTranslation();
    const endEvent = getEnd(events) as CaseRenamingEndEvent;
    return (
        <div>
            <span className={"animate-fade"}>Renaming case ...</span>
            {endEvent !== undefined && (
                <span>
                    <span className={"font-bold text-green-500 animate-fade"}> finished</span>
                    <span>.</span>
                </span>
            )}
        </div>
    )
}

export const rendererMap = new Map<string, React.FC<{ events: WsEvent[] }>>(
    [
        [Activity.TECH_DOC_SEARCH, SearchWaitingProgress],
        [Activity.COMM_CASE_SEARCH, SearchWaitingProgress],
        [Activity.PARALLEL_SEARCH, ParallelSearchProgress],
        [Activity.RESPONSE_GENERATION, ResponseGenerationProgress],
        [Activity.VERSION_INFO, VersionInformationProgress],
        [Activity.RERANKING, RerankingProgress],
        [Activity.ROUTE_SELECTION, RouteSelectionProgress],
        [Activity.CASE_RENAMING, CaseRenamingProgress]
    ]
)